import React, { useEffect, useState } from 'react';
import {
  Button,
  Table,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tooltip,
  Badge,
  Card,
  Row,
  Col,
  Typography,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  SendOutlined,
  StopOutlined,
  DeleteOutlined,
  MailOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import type { EmailSchedule, EmailType, EmailFrequency, RecipientType, EmailScheduleStatus } from '../types';
import { staticApi } from '../utils/staticApi';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EMAIL_TYPE_LABELS: Record<EmailType, string> = {
  FINANCIAL_STATEMENT: 'Financial Statement',
  FEE_REMINDER: 'Fee Reminder',
  VIOLATION_NOTICE: 'Violation Notice',
  MAINTENANCE_NOTICE: 'Maintenance Notice',
  MEETING_NOTICE: 'Meeting Notice',
  GENERAL_ANNOUNCEMENT: 'General Announcement',
  ARREARS_NOTICE: 'Arrears Notice',
};

const FREQUENCY_LABELS: Record<EmailFrequency, string> = {
  IMMEDIATE: 'Send Immediately',
  ONCE: 'Once (scheduled)',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
};

const RECIPIENT_LABELS: Record<RecipientType, string> = {
  ALL_UNITS: 'All Units',
  SPECIFIC_UNITS: 'Specific Units',
  UNITS_IN_ARREARS: 'Units in Arrears',
};

const STATUS_CONFIG: Record<EmailScheduleStatus, { color: string; icon: React.ReactNode }> = {
  SCHEDULED: { color: 'blue', icon: <ClockCircleOutlined /> },
  SENDING: { color: 'processing', icon: <SyncOutlined spin /> },
  SENT: { color: 'success', icon: <CheckCircleOutlined /> },
  FAILED: { color: 'error', icon: <ExclamationCircleOutlined /> },
  CANCELLED: { color: 'default', icon: <CloseCircleOutlined /> },
};

export const EmailCenter: React.FC = () => {
  const { emailSchedules, fetchEmailSchedules, createEmailSchedule, cancelEmailSchedule, deleteEmailSchedule, sendEmailNow } = useCondoStore();
  const { currentCondo } = useAuthStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const recipientType = Form.useWatch('recipientType', form);
  const frequency = Form.useWatch('frequency', form);

  useEffect(() => {
    if (currentCondo?.id) {
      fetchEmailSchedules(currentCondo.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchEmailSchedules is a stable zustand action
  }, [currentCondo?.id]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const schedule: EmailSchedule = {
        condoId: currentCondo!.id,
        subject: values.subject,
        body: values.body,
        emailType: values.emailType,
        recipientType: values.recipientType,
        recipientUnitIds: values.recipientUnitIds || undefined,
        frequency: values.frequency,
        scheduledAt: values.scheduledAt ? values.scheduledAt.toISOString() : undefined,
      };

      await createEmailSchedule(schedule);
      staticApi.message.success('Email schedule created successfully');
      setModalOpen(false);
      form.resetFields();
    } catch (err: any) {
      if (err?.errorFields) return; // validation error, don't show toast
      staticApi.message.error(err?.message || 'Failed to create email schedule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendNow = async (id: string) => {
    try {
      setSendingId(id);
      await sendEmailNow(id);
      staticApi.message.success('Email dispatched successfully');
    } catch (err: any) {
      staticApi.message.error(err?.message || 'Failed to send email');
    } finally {
      setSendingId(null);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await cancelEmailSchedule(id);
      staticApi.message.success('Schedule cancelled');
    } catch (err: any) {
      staticApi.message.error(err?.message || 'Failed to cancel schedule');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteEmailSchedule(id);
      staticApi.message.success('Schedule deleted');
    } catch (err: any) {
      staticApi.message.error(err?.message || 'Failed to delete schedule');
    }
  };

  const columns = [
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'emailType',
      key: 'emailType',
      render: (v: EmailType) => EMAIL_TYPE_LABELS[v] ?? v,
      width: 160,
    },
    {
      title: 'Recipients',
      dataIndex: 'recipientType',
      key: 'recipientType',
      render: (v: RecipientType, row: EmailSchedule) => (
        <span>
          {RECIPIENT_LABELS[v] ?? v}
          {v === 'SPECIFIC_UNITS' && row.recipientUnitIds && (
            <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>
              ({row.recipientUnitIds})
            </Text>
          )}
        </span>
      ),
      width: 180,
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (v: EmailFrequency) => FREQUENCY_LABELS[v] ?? v,
      width: 140,
    },
    {
      title: 'Scheduled At',
      dataIndex: 'scheduledAt',
      key: 'scheduledAt',
      render: (v: string) => v ? dayjs(v).format('MMM D, YYYY h:mm A') : '—',
      width: 160,
    },
    {
      title: 'Last Sent',
      dataIndex: 'lastSentAt',
      key: 'lastSentAt',
      render: (v: string) => v ? dayjs(v).format('MMM D, YYYY h:mm A') : '—',
      width: 160,
    },
    {
      title: 'Sends',
      dataIndex: 'sendCount',
      key: 'sendCount',
      width: 70,
      align: 'center' as const,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: EmailScheduleStatus) => {
        const cfg = STATUS_CONFIG[v] ?? { color: 'default', icon: null };
        return (
          <Badge
            status={v === 'SENDING' ? 'processing' : v === 'SENT' ? 'success' : v === 'FAILED' ? 'error' : v === 'CANCELLED' ? 'default' : 'processing'}
            text={
              <Tag color={cfg.color} icon={cfg.icon} style={{ margin: 0 }}>
                {v}
              </Tag>
            }
          />
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: any, row: EmailSchedule) => (
        <Space size="small">
          {(row.status === 'SCHEDULED' || row.status === 'FAILED') && (
            <Tooltip title="Send Now">
              <Button
                size="small"
                type="primary"
                icon={<SendOutlined />}
                loading={sendingId === row.id}
                onClick={() => handleSendNow(row.id!)}
              />
            </Tooltip>
          )}
          {row.status === 'SCHEDULED' && (
            <Popconfirm title="Cancel this schedule?" onConfirm={() => handleCancel(row.id!)}>
              <Tooltip title="Cancel">
                <Button size="small" icon={<StopOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
          {(row.status === 'SENT' || row.status === 'CANCELLED' || row.status === 'FAILED') && (
            <Popconfirm title="Delete this schedule?" onConfirm={() => handleDelete(row.id!)}>
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Summary counts
  const scheduled = emailSchedules.filter(s => s.status === 'SCHEDULED').length;
  const sent = emailSchedules.filter(s => s.status === 'SENT').length;
  const failed = emailSchedules.filter(s => s.status === 'FAILED').length;

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <MailOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Email Center
          </Title>
          <Text type="secondary">Schedule and manage email communications to unit owners</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
          >
            New Email Schedule
          </Button>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff' }}>{scheduled}</div>
              <div style={{ color: '#666' }}>Scheduled</div>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>{sent}</div>
              <div style={{ color: '#666' }}>Sent</div>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#ff4d4f' }}>{failed}</div>
              <div style={{ color: '#666' }}>Failed</div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          dataSource={emailSchedules}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 15 }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: 'No email schedules yet. Click "New Email Schedule" to create one.' }}
        />
      </Card>

      <Modal
        title="New Email Schedule"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Create Schedule"
        width={680}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="emailType"
                label="Email Type"
                rules={[{ required: true, message: 'Select an email type' }]}
              >
                <Select placeholder="Select type">
                  {(Object.entries(EMAIL_TYPE_LABELS) as [EmailType, string][]).map(([k, v]) => (
                    <Option key={k} value={k}>{v}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="frequency"
                label="Send Frequency"
                rules={[{ required: true, message: 'Select a frequency' }]}
              >
                <Select placeholder="Select frequency">
                  {(Object.entries(FREQUENCY_LABELS) as [EmailFrequency, string][]).map(([k, v]) => (
                    <Option key={k} value={k}>{v}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {frequency && frequency !== 'IMMEDIATE' && (
            <Form.Item
              name="scheduledAt"
              label={frequency === 'ONCE' ? 'Send At' : 'First Send At'}
              rules={[{ required: true, message: 'Select a date and time' }]}
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                disabledDate={d => d && d < dayjs().startOf('day')}
              />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="recipientType"
                label="Recipients"
                rules={[{ required: true, message: 'Select recipients' }]}
              >
                <Select placeholder="Select recipients">
                  {(Object.entries(RECIPIENT_LABELS) as [RecipientType, string][]).map(([k, v]) => (
                    <Option key={k} value={k}>{v}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {recipientType === 'SPECIFIC_UNITS' && (
              <Col span={12}>
                <Form.Item
                  name="recipientUnitIds"
                  label="Unit Numbers"
                  extra="Comma-separated, e.g. 101, 202, 305"
                  rules={[{ required: true, message: 'Enter at least one unit number' }]}
                >
                  <Input placeholder="101, 202, 305" />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Enter a subject' }]}
          >
            <Input placeholder="e.g. Monthly Fee Statement — April 2026" maxLength={200} />
          </Form.Item>

          <Form.Item
            name="body"
            label="Message Body"
            rules={[{ required: true, message: 'Enter a message body' }]}
          >
            <TextArea
              rows={8}
              placeholder="Dear Resident,&#10;&#10;Please find attached your..."
              maxLength={5000}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
